import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useQueries } from '@tanstack/react-query';
import { Splitter } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { parseFqnToPathParts } from '@/shared/lib/format';
import { glossaryKeys } from '@/shared/lib/query/queryKeys/glossary';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import { fetchGlossaryTermBasic } from '@/features/glossary/api/fetchGlossaryTermBasic';
import { useGlossaryTermCategoryInfo } from '@/features/glossary/model/useGlossaryTermCategoryInfo';
import { GlossaryCategoryFormModal } from '@/features/glossary/ui/GlossaryCategoryFormModal';
import { GlossaryCategoryTree } from '@/features/glossary/ui/GlossaryCategoryTree';

import { GlossaryDetailContent } from '@/widgets/glossary';

import styles from './GlossaryPage.module.css';

const SPLITTER_SIZES = {
  DEFAULT: 300,
  MIN: 250,
  MAX: 500,
} as const;

export const GlossaryPage = () => {
  const navigate = useNavigate();
  const { termCategory, termFqn, tab } = useParams<{ termCategory?: string; termFqn?: string; tab?: string }>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshParentFqn, setRefreshParentFqn] = useState<string | null>(null);

  const { data: categoryData, isLoading: isLoadingCategory } = useGlossaryTermCategoryInfo({
    category: termCategory || '',
  });

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleTermCreated = useCallback((parentFqn: string) => {
    setRefreshParentFqn(parentFqn);
  }, []);

  const handleRefreshComplete = useCallback(() => {
    setRefreshParentFqn(null);
  }, []);

  const fullTermFqn = useMemo(() => {
    if (!termCategory) return undefined;
    if (!termFqn) return termCategory;
    return `${termCategory}.${termFqn}`;
  }, [termCategory, termFqn]);

  useEffect(() => {
    if (termFqn && termCategory && !tab) {
      navigate(`/glossary/${termCategory}/${termFqn}/glossary_terms`, { replace: true });
    }
  }, [termFqn, termCategory, tab, navigate]);

  const allTermFqns = useMemo(() => {
    if (!termFqn || !termCategory) return [];
    const termParts = parseFqnToPathParts(termFqn, true);

    return termParts.map((_, index) => {
      const partialPath = termParts.slice(0, index + 1).join('.');
      return `${termCategory}.${partialPath}`;
    });
  }, [termCategory, termFqn]);

  const allTermsQueries = useQueries({
    queries: allTermFqns.map((fqn) => ({
      queryKey: glossaryKeys.termBasic(fqn),
      queryFn: () => fetchGlossaryTermBasic(fqn),
      enabled: !!fqn,
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  const isLoadingTerms = allTermsQueries.some((query) => query.isLoading);
  const isLoadingBreadcrumb = isLoadingCategory || isLoadingTerms;

  const categoryDisplayName = categoryData?.displayName || categoryData?.name || termCategory || '';

  const breadcrumbItems = useMemo<BreadcrumbItemType[]>(() => {
    if (isLoadingBreadcrumb) return [];

    if (!termCategory) return [];

    const items = [];

    if (termFqn) {
      const termParts = termFqn.split('.');

      items.push({
        title: (
          <Link to={`/glossary/${termCategory}`} className={styles.breadcrumbItem}>
            <EllipsisText text={categoryDisplayName} className={styles.breadcrumbText} />
          </Link>
        ),
      });

      termParts.forEach((part, index) => {
        const isLast = index === termParts.length - 1;
        const partialPath = termParts.slice(0, index + 1).join('.');
        const termData = allTermsQueries[index]?.data;
        const displayName = termData?.displayName || termData?.name || part;

        if (isLast) {
          items.push({
            title: (
              <span className={styles.breadcrumbItem}>
                <EllipsisText text={displayName} className={styles.breadcrumbText} />
              </span>
            ),
          });
        } else {
          items.push({
            title: (
              <Link to={`/glossary/${termCategory}/${partialPath}`} className={styles.breadcrumbItem}>
                <EllipsisText text={displayName} className={styles.breadcrumbText} />
              </Link>
            ),
          });
        }
      });
    } else {
      items.push({
        title: (
          <span className={styles.breadcrumbItem}>
            <EllipsisText text={categoryDisplayName} className={styles.breadcrumbText} />
          </span>
        ),
      });
    }

    return items;
  }, [termCategory, termFqn, categoryDisplayName, allTermsQueries, isLoadingBreadcrumb]);

  return (
    <div className={styles.glossaryPage}>
      <Splitter className={styles.splitter}>
        <Splitter.Panel
          defaultSize={SPLITTER_SIZES.DEFAULT}
          min={SPLITTER_SIZES.MIN}
          max={SPLITTER_SIZES.MAX}
          resizable>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <GlossaryCategoryTree.Error
                {...props}
                title={NOTIFICATION_MESSAGES.GLOSSARY_CATEGORY_ERROR}
                description={NOTIFICATION_MESSAGES.GLOSSARY_CATEGORY_ERROR_DESC}
              />
            )}>
            <GlossaryCategoryTree.Tree
              selectedTermFqn={fullTermFqn}
              onAddClick={handleOpenCreateModal}
              refreshParentFqn={refreshParentFqn}
              onRefreshComplete={handleRefreshComplete}
            />
          </ErrorBoundary>
        </Splitter.Panel>

        <Splitter.Panel>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <GlossaryDetailContent.Error
                {...props}
                title={NOTIFICATION_MESSAGES.GLOSSARY_DETAIL_COMPONENT_ERROR}
                description={NOTIFICATION_MESSAGES.GLOSSARY_DETAIL_COMPONENT_ERROR_DESC}
              />
            )}>
            <Suspense fallback={<GlossaryDetailContent.Suspense />}>
              <div className={styles.mainContentArea}>
                <GlossaryDetailContent.Main
                  termCategory={termCategory || null}
                  termFqn={termFqn || null}
                  currentTab={tab || 'glossary_terms'}
                  breadcrumbItems={breadcrumbItems}
                  onTermCreated={handleTermCreated}
                />
              </div>
            </Suspense>
          </ErrorBoundary>
        </Splitter.Panel>
      </Splitter>

      <GlossaryCategoryFormModal open={isCreateModalOpen} mode="create" onClose={handleCloseCreateModal} />
    </div>
  );
};
